"use client"

import { useState } from "react"
import { CheckCircle2 } from "lucide-react"

import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { SectionEyebrow } from "@/components/marketing/section-eyebrow"
import {
  deferredChargeLine,
  formatAmount,
  recurringChargeLine,
  type DeferredStart,
  type OfferCopy,
  type PromoProps,
} from "./checkout-shared"

/** The one line visible before a click, his exact words, and nothing else —
 *  "Keep only that text until clicked. Don't expose the field by default."
 *  Clicking reveals a small input; applying resolves server-side
 *  (resolveHandoutCoupon) and either lowers "Total due today" above with no
 *  code name or percentage anywhere, or shows one plain error line. */
function PromoCodeField({ onApply, applying, error, applied }: PromoProps) {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState("")

  if (applied) {
    return <p className="text-[0.82rem] text-white/60">Code applied.</p>
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="self-start text-[0.82rem] font-medium text-white/60 underline decoration-white/30 underline-offset-2 transition-colors hover:text-white/85"
      >
        Have a promo code?
      </button>
    )
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        const trimmed = value.trim()
        if (!trimmed || applying) return
        onApply(trimmed)
      }}
      className="flex flex-col gap-2"
    >
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Enter code"
          aria-label="Promo code"
          disabled={applying}
          className="border-white/20 bg-white text-[var(--color-ink)]"
        />
        <button
          type="submit"
          disabled={!value.trim() || applying}
          className={cn(
            "shrink-0 rounded-[var(--radius-md)] border border-white/25 px-4 text-[0.85rem] font-semibold text-white transition-colors hover:bg-white/10 disabled:opacity-50",
          )}
        >
          {applying ? "Applying…" : "Apply"}
        </button>
      </div>
      {error && <p className="text-[0.82rem] leading-[1.4] text-[#ffb4a8]">{error}</p>}
    </form>
  )
}

/** Left column: confirm what the buyer selected (title, one bold what-it-is
 * line, checkmark features, price) plus the scheduling reassurance. Owner
 * ruling stripped everything else — no next-steps strip, no discount copy;
 * the one carve-out is the collapsed typed promo box, see PromoCodeField.
 * Dark (bg-[--color-ink-lifted]) to match the Stripe hosted-checkout
 * structural reference. */
export function OrderSummary({
  offer,
  amountCents,
  currency,
  recurringInterval,
  promo,
  deferredStart = null,
}: {
  offer: OfferCopy
  /** Null only during the pre-paint of a plan whose amount the server chose
   *  not to guess (recurring tiers). */
  amountCents: number | null
  currency: string
  /** "a week" on a recurring tier, null on a one-time offer. */
  recurringInterval: string | null
  /** Null during pre-paint (no PaymentIntent yet to reprice) and on a
   *  recurring tier (a percent-off cannot reach a later invoice). */
  promo?: PromoProps | null
  /** A weekly plan that starts when his prepaid block ends: "Total due
   *  today" is $0 and the line under it names the first charge's date. */
  deferredStart?: DeferredStart | null
}) {

  return (
    <div className="flex flex-col gap-6 rounded-[var(--radius-lg)] bg-[var(--color-ink-lifted)] p-6 md:p-7">
      <div>
        <SectionEyebrow size="sm" as="span" tone="white-soft">
          Your order
        </SectionEyebrow>
        <h2 className="serious-display mt-2 text-[clamp(1.4rem,2.4vw,1.8rem)] leading-tight text-white">
          {offer.title}
        </h2>
      </div>

      {offer.included.length > 0 && (
        <ul className="flex flex-col gap-3 border-y border-white/12 py-5">
          {offer.included.map((item) => (
            <li key={item} className="flex items-start gap-2.5 text-[0.92rem] leading-[1.5] text-white/90">
              <CheckCircle2 aria-hidden size={16} className="mt-0.5 shrink-0 text-[var(--color-vermilion)]" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between">
          <span className="text-[0.85rem] font-semibold uppercase tracking-[0.06em] text-white/55">
            Total due today
          </span>
          <span className="serious-display text-[1.5rem] text-white">
            {amountCents == null ? "…" : formatAmount(amountCents, currency)}
          </span>
        </div>
        {/* On a weekly tier, "Total due today" beside $300 reads as the whole
            cost of the program. The cadence has to sit next to the number, not
            three sections up in the subtitle, and it has to be the offer's own
            cadence: a membership is never told it renews next week. */}
        {recurringInterval && amountCents != null && (
          <p className="text-[0.85rem] leading-[1.5] text-white/60">
            {deferredStart
              ? deferredChargeLine(formatAmount(deferredStart.recurringAmount, currency), deferredStart.firstChargeAt)
              : recurringChargeLine(formatAmount(amountCents, currency), recurringInterval)}
          </p>
        )}
      </div>

      {/* The one carve-out from "never offer a discount on the checkout page":
          a typed hand-out code, collapsed to a single line until clicked. */}
      {promo && <PromoCodeField {...promo} />}

      {/* Scheduling reassurance (his point 5) — only rendered when the copy
          module says it is operationally true for this offer. */}
      {offer.scheduleNote && (
        <p className="text-[0.88rem] leading-[1.55] text-white/75">{offer.scheduleNote}</p>
      )}

    </div>
  )
}
